import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { shopSellerService } from "../services/service/shopSellerService";

const EMPTY_PARAMS = {};
const SHOP_SELLERS_KEY = ["shop-sellers"];
const MY_SHOPS_SELLERS_KEY = ["my-shops-sellers"];
const AVAILABLE_SELLERS_KEY = ["shop-sellers", "available"];

export const useShopSellers = (shopId, initialParams = EMPTY_PARAMS) => {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const params = useMemo(() => ({
        shopId,
        page: currentPage,
        per_page: pageSize,
        search: searchTerm,
        status: statusFilter !== "all" ? statusFilter : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        ...initialParams,
    }), [shopId, currentPage, pageSize, searchTerm, statusFilter, startDate, endDate, initialParams]);

    const query = useQuery({
        queryKey: [...SHOP_SELLERS_KEY, "shop", shopId, params],
        queryFn: () => shopSellerService.getAll(params),
        enabled: Boolean(shopId),
        staleTime: Infinity,
    });

    const invalidate = useCallback(() =>
        queryClient.invalidateQueries({ queryKey: SHOP_SELLERS_KEY }),
        [queryClient]
    );

    const createSeller = useCallback(async (data) => {
        const response = await shopSellerService.create(data);
        await invalidate();
        return response;
    }, [invalidate]);

    const updateSeller = useCallback(async (id, data) => {
        const response = await shopSellerService.update(id, data);
        await invalidate();
        return response;
    }, [invalidate]);

    const toggleSellerStatus = useCallback(async (id) => {
        const response = await shopSellerService.toggleStatus(id);
        await invalidate();
        return response;
    }, [invalidate]);

    const setSellerStatus = useCallback(async (id, status) => {
        const response = await shopSellerService.setStatus(id, status);
        await invalidate();
        return response;
    }, [invalidate]);

    const getTotalByShop = useCallback(async (shopId) => {
        try {
            const response = await shopSellerService.getTotalByShop(shopId);
            return response.data?.data || {};
        } catch (error) {
            throw error;
        }
    }, []);

    const getAvailableSellers = useCallback(async (params = {}) => {
        try {
            const response = await shopSellerService.getAvailableSellers(params);
            return response.data?.data || [];
        } catch (error) {
            throw error;
        }
    }, []);

    return {
        sellers: query.data?.data || [],
        pagination: query.data || null,
        loading: query.isLoading || query.isFetching,
        error: query.error,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        refresh: query.refetch,
        createSeller,
        updateSeller,
        toggleSellerStatus,
        setSellerStatus,
        getTotalByShop,
        getAvailableSellers,
    };
};

export const useMyShopsSellers = (initialParams = EMPTY_PARAMS) => {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [shopFilter, setShopFilter] = useState("");

    const params = useMemo(() => ({
        shopId: shopFilter || undefined,
        page: currentPage,
        per_page: pageSize,
        search: searchTerm || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        ...initialParams,
    }), [
        shopFilter,
        currentPage,
        pageSize,
        searchTerm,
        statusFilter,
        startDate,
        endDate,
        initialParams,
    ]);

    const query = useQuery({
        queryKey: [...MY_SHOPS_SELLERS_KEY, params],
        queryFn: () => shopSellerService.getMyShops(params),
        staleTime: Infinity,
    });

    const invalidate = useCallback(() =>
        queryClient.invalidateQueries({ queryKey: MY_SHOPS_SELLERS_KEY }),
        [queryClient]
    );

    const createSeller = useCallback(async (data) => {
        const response = await shopSellerService.create(data);
        await invalidate();
        return response;
    }, [invalidate]);

    const updateSeller = useCallback(async (id, data) => {
        const response = await shopSellerService.update(id, data);
        await invalidate();
        return response;
    }, [invalidate]);

    const toggleSellerStatus = useCallback(async (id) => {
        const response = await shopSellerService.toggleStatus(id);
        await invalidate();
        return response;
    }, [invalidate]);

    const setSellerStatus = useCallback(async (id, status) => {
        const response = await shopSellerService.setStatus(id, status);
        await invalidate();
        return response;
    }, [invalidate]);

    const getTotalByShop = useCallback(async (shopId) => {
        try {
            const response = await shopSellerService.getTotalByShop(shopId);
            return response.data?.data || {};
        } catch (error) {
            throw error;
        }
    }, []);

    return {
        sellers: query.data?.data || [],
        pagination: query.data?.meta || null,
        loading: query.isLoading || query.isFetching,
        error: query.error,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        shopFilter,
        setShopFilter,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        refresh: query.refetch,
        createSeller,
        updateSeller,
        toggleSellerStatus,
        setSellerStatus,
        getTotalByShop,
    };
};

export const useAvailableSellers = (shopId, search = "", enabled = true) => {
    const query = useQuery({
        queryKey: [...AVAILABLE_SELLERS_KEY, shopId, search],
        queryFn: () =>
            shopSellerService.getAvailableSellers({
                shopId,
                search: search?.trim() || undefined,
            }),
        enabled: Boolean(shopId) && enabled,
        staleTime: 30 * 1000,
    });

    return {
        availableSellers: query.data?.data || [],
        loading: query.isLoading || query.isFetching,
        error: query.error,
        refetch: query.refetch,
    };
};

export const useShopSeller = useShopSellers;
export const useSellers = useShopSellers;